import { Target, Eye, Users, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const AboutPage = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  const values = [
    {
      icon: Target,
      title: 'স্বচ্ছতা',
      titleEn: 'Transparency',
      description: 'সকল তথ্য সঠিক ও যাচাইকৃত উৎস থেকে সংগ্রহ করা হয়।',
      descriptionEn: 'All information is collected from verified sources.',
    },
    {
      icon: Users,
      title: 'সহযোগিতা',
      titleEn: 'Support',
      description: 'আমরা সাধারণ মানুষের পাশে থাকতে প্রতিশ্রুতিবদ্ধ।',
      descriptionEn: 'We are committed to helping common people.',
    },
    {
      icon: Eye,
      title: 'বিশ্বস্ততা',
      titleEn: 'Trust',
      description: 'যাচাইকৃত সেবাদাতাদের সাথে সংযুক্ত করি।',
      descriptionEn: 'We connect you with verified service providers.',
    },
    {
      icon: Heart,
      title: 'সেবা',
      titleEn: 'Service',
      description: 'জনগণের সেবায় নিবেদিত একটি প্ল্যাটফর্ম।',
      descriptionEn: 'A platform dedicated to serving the people.',
    },
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <SEO 
        title={language === 'en' ? 'About Us' : 'আমাদের সম্পর্কে'}
        description={language === 'en'
          ? 'Learn about Bhumi - Bangladesh\'s trusted platform for land information, guides, and verified service providers.'
          : 'ভূমি সম্পর্কে জানুন - বাংলাদেশের জমি সংক্রান্ত তথ্য, গাইড এবং যাচাইকৃত সেবাদাতাদের বিশ্বস্ত প্ল্যাটফর্ম।'
        }
        path="/about"
      />
      <Navbar isDark={isDark} />

      {/* Hero */}
      <section className={`pt-24 pb-16 ${isDark ? 'bg-[#111]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="about-title">
                {t('about.title')} <span className="text-[#2E7D32]">{t('about.story')}</span>
              </h1>
              <p className={`text-lg mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                বাংলাদেশে জমি সংক্রান্ত সমস্যা একটি জটিল বিষয়। নামজারি, খতিয়ান, দলিল - 
                এসব নিয়ে সাধারণ মানুষ প্রায়ই বিভ্রান্তিতে পড়েন। 
              </p>
              <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <strong>ভূমি</strong> এই সমস্যার সমাধান দিতে তৈরি হয়েছে। আমরা চাই 
                প্রতিটি বাংলাদেশী সহজে জমি সংক্রান্ত সঠিক তথ্য পান এবং 
                বিশ্বস্ত সেবাদাতার সাথে সংযুক্ত হতে পারেন।
              </p>
              <Link to="/guides">
                <Button className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-full px-8 py-3 font-semibold">
                  {t('nav.guides')}
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div 
                className="aspect-square rounded-2xl bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1746591569629-6ebacaeda7ce?crop=entropy&cs=srgb&fm=jpg&q=85)',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-[#2E7D32]/40 to-transparent rounded-2xl" />
              </div>
              {/* Floating card */}
              <div className={`absolute -bottom-6 -left-6 rounded-xl p-4 shadow-lg border ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="text-3xl font-bold text-[#2E7D32] font-data">২০২৪</div>
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>প্রতিষ্ঠাকাল</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className={`py-16 ${isDark ? 'bg-[#0D330F]' : 'bg-[#E8F5E9]'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('about.mission')}</h2>
          <p className={`text-xl leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            "বাংলাদেশের প্রতিটি নাগরিককে জমি সংক্রান্ত সঠিক তথ্য ও বিশ্বস্ত সেবা 
            প্রদানের মাধ্যমে ভূমি সংক্রান্ত জটিলতা দূর করা।"
          </p>
        </div>
      </section>

      {/* Values */}
      <section className={`py-16 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('about.values')}</h2>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('about.valuesDesc')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => (
              <div
                key={value.title}
                className={`rounded-xl p-6 text-center transition-colors ${isDark ? 'bg-[#111] hover:bg-[#2E7D32]/10' : 'bg-gray-50 hover:bg-[#E8F5E9]'}`}
              >
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-[#2E7D32]/20' : 'bg-[#E8F5E9]'}`}>
                  <value.icon className="text-[#2E7D32]" size={28} />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{value.title}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className={`py-16 ${isDark ? 'bg-[#111]' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('about.whatWeOffer')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className={`rounded-xl p-6 border ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'}`}>
              <div className="text-4xl font-bold text-[#FBC02D] font-data mb-2">৫০+</div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('stats.guides')}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                নামজারি থেকে শুরু করে রেজিস্ট্রেশন পর্যন্ত সকল প্রক্রিয়ার বিস্তারিত গাইড।
              </p>
            </div>
            <div className={`rounded-xl p-6 border ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'}`}>
              <div className="text-4xl font-bold text-[#FBC02D] font-data mb-2">১০০+</div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('stats.providers')}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                অভিজ্ঞ উকিল, এজেন্ট ও ডকুমেন্ট সহকারীদের নেটওয়ার্ক।
              </p>
            </div>
            <div className={`rounded-xl p-6 border ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'}`}>
              <div className="text-4xl font-bold text-[#FBC02D] font-data mb-2">২৪/৭</div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>তথ্য অ্যাক্সেস</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                যেকোনো সময় আমাদের তথ্য ভান্ডার থেকে প্রয়োজনীয় তথ্য পান।
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#2E7D32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            {t('about.joinUs')}
          </h2>
          <p className="text-green-100 mb-8">
            {t('about.joinUsDesc')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-[#2E7D32] hover:bg-gray-100 rounded-full px-8 py-3 font-semibold">
                {t('nav.contact')}
              </Button>
            </Link>
            <Link to="/services">
              <Button className="bg-[#FBC02D] text-gray-900 hover:bg-[#F9A825] rounded-full px-8 py-3 font-semibold">
                {t('landing.findProviders')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default AboutPage;
